/**
 * MobileTopBar — the top band a phone gets where the navbar is hidden.
 *
 * THE DEFECT THIS CLOSES (obs. 7). `PageShell` composed exactly three bands:
 * the navbar (`hidden md:block`), the footer (`hidden md:block`) and the tab
 * bar (`md:hidden`). Below `md` that leaves a `focus` or `article` page — a
 * listing detail, an estate, a district, settings, support, a conversation
 * thread, a blog post — with NO top band at all and no in-page way back:
 * measured on the hermetic fleet at 393x852, the listing detail rendered zero
 * visible `header` elements and zero back controls. The only mobile top bar in
 * the app was the browse hub's own `ListingsTopBar`, whose back control always
 * went to the locale home whatever the reader had come from.
 *
 * The remedy is not a header component each page can remember to render. It is
 * one more COLUMN of the chrome table (`ChromeSpec.mobileHeader`), so the band
 * is a property of the kind a route declares, rendered here once. A page
 * module could not mount it anyway: `page` may not import `infra` in the
 * element DAG, which is exactly why the shell owns it.
 *
 * ## What the band is, and is not
 *
 * A back control, the wordmark, and a spacer of the back control's own width
 * so the wordmark is optically centred — not a title. A per-page title would
 * need every page to supply one (a second declaration to forget) and would
 * duplicate the `h1` a content page already renders one scroll-line below.
 *
 * `sticky top-0`, so it stays in FLOW: it needs no spacer the way the `fixed`
 * tab bar needs `pb-16`, which is why `ChromeSpec` has no `pt-*` twin of that
 * derivation. `Z_LAYERS.navbar` is the same page layer the desktop navbar
 * takes — it is the same band at the same edge, one breakpoint apart.
 */
import { AppLogo } from '@/shared/components/app';
import { BackLink } from '@/shared/components/BackLink';
import type { Route } from '@letsgethome/routes';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

export interface MobileTopBarProps {
  /** Where a COLD back goes — the surface's declared logical parent. */
  readonly backTo: Route;
}

export function MobileTopBar({ backTo }: MobileTopBarProps) {
  return (
    <header
      data-slot="mobile-top-bar"
      className={cn(
        'sticky top-0 md:hidden',
        Z_LAYERS.navbar.className,
        'h-[var(--mobile-header-height)] border-b border-border bg-background'
      )}
    >
      <div className="grid h-full grid-cols-[40px_1fr_40px] items-center gap-2 px-2">
        <BackLink backTo={backTo} />
        <div className="flex min-w-0 justify-center">
          <AppLogo />
        </div>
        {/* The optical counterweight to the back control. `aria-hidden`
            because it carries nothing: a reader hears one control here. */}
        <div aria-hidden="true" />
      </div>
    </header>
  );
}
